/**
 * AddRatingModal Component
 *
 * Modal dialog for adding a new rating to a recipe.
 * Features:
 * - Family member select dropdown
 * - RatingInput for star selection
 * - Optional notes textarea
 * - Cancel and Save buttons
 * - Submits via POST to /food/recipes/:id/ratings
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { FamilyMember } from '@/types/food';
import { RatingInput } from './RatingInput';

interface AddRatingModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when rating is saved successfully */
  onSave: () => void;
  /** Recipe ID to add rating for */
  recipeId: number;
  /** List of all family members */
  familyMembers: FamilyMember[];
  /** IDs of family members who have already rated */
  existingMemberIds: number[];
}

function XIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export function AddRatingModal({
  isOpen,
  onClose,
  onSave,
  recipeId,
  familyMembers,
  existingMemberIds,
}: AddRatingModalProps) {
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Filter to only show members who haven't rated yet
  const availableMembers = familyMembers.filter(
    (m) => !existingMemberIds.includes(m.id)
  );

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedMemberId(availableMembers.length === 1 ? availableMembers[0].id : null);
      setRating(null);
      setNotes('');
      setError(null);
    }
  }, [isOpen, availableMembers.length]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSaving, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element
    firstElement?.focus();

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && !isSaving) {
        onClose();
      }
    },
    [onClose, isSaving]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!selectedMemberId || !rating) {
        setError('Please select a family member and rating');
        return;
      }

      setIsSaving(true);
      setError(null);

      try {
        const response = await fetch(`/food/recipes/${recipeId}/ratings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
          },
          body: JSON.stringify({
            rating: {
              family_member_id: selectedMemberId,
              rating,
              notes: notes.trim() || null,
            },
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to save rating');
        }

        onSave();
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save rating');
      } finally {
        setIsSaving(false);
      }
    },
    [selectedMemberId, rating, notes, recipeId, onSave, onClose]
  );

  const canSubmit = selectedMemberId !== null && rating !== null && !isSaving;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      data-testid="add-rating-modal-backdrop"
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-rating-modal-title"
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-rating-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 id="add-rating-modal-title" className="text-lg font-semibold text-gray-900">Add Rating</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            aria-label="Close modal"
          >
            <XIcon />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Family Member Select */}
            <div>
              <label
                htmlFor="family-member"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Family Member
              </label>
              <select
                id="family-member"
                value={selectedMemberId ?? ''}
                onChange={(e) => setSelectedMemberId(e.target.value ? Number(e.target.value) : null)}
                disabled={isSaving}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                data-testid="family-member-select"
              >
                <option value="">Select a family member...</option>
                {availableMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating
              </label>
              <RatingInput
                value={rating}
                onChange={setRating}
                size="lg"
                disabled={isSaving}
              />
            </div>

            {/* Notes */}
            <div>
              <label
                htmlFor="notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Notes <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSaving}
                rows={3}
                placeholder="Any thoughts about the recipe..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed resize-none"
                data-testid="notes-textarea"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-rating-button"
            >
              {isSaving ? 'Saving...' : 'Save Rating'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
